import { useState } from "react";
import { Plus, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useImageStylePresets } from "@/hooks/useImageStylePresets";

/**
 * One-click "image looks" (radius + shadow combo) for an image editor
 * panel. Built-in presets are always there; admins can also save whatever
 * combo is currently applied as a new named preset, and delete their own.
 */
export function ImagePresetsRow({ radius, shadow, onApply, testIdPrefix = "image-preset" }) {
  const { presets, savePreset, deletePreset } = useImageStylePresets();
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [saving, setSaving] = useState(false);

  const isActive = (p) => Number(p.radius) === Number(radius) && p.shadow === shadow;

  const handleSave = async () => {
    const name = presetName.trim();
    if (!name) return;
    setSaving(true);
    const ok = await savePreset(name, radius, shadow);
    setSaving(false);
    if (ok) {
      setPresetName("");
      setShowSaveForm(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>Image Looks</Label>
      <div className="flex flex-wrap gap-1.5">
        {presets.map((p) => (
          <div key={p.preset_id} className="relative group">
            <Button
              type="button"
              size="sm"
              variant={isActive(p) ? "default" : "outline"}
              className="rounded-none text-xs"
              onClick={() => onApply(p.radius, p.shadow)}
              data-testid={`${testIdPrefix}-${p.preset_id}`}
            >
              {p.name}
            </Button>
            {!p.builtin && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${p.name}" preset?`)) deletePreset(p.preset_id); }}
                className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                title="Delete preset"
                data-testid={`${testIdPrefix}-${p.preset_id}-delete`}
              >
                <X size={10} />
              </button>
            )}
          </div>
        ))}
      </div>

      {showSaveForm ? (
        <div className="flex gap-2">
          <Input
            placeholder="Preset name"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            className="rounded-none h-8 text-xs"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            data-testid={`${testIdPrefix}-name-input`}
          />
          <Button
            type="button" size="sm" className="rounded-none h-8 px-2" disabled={saving || !presetName.trim()}
            onClick={handleSave} data-testid={`${testIdPrefix}-save-confirm`}
          >
            <Check size={14} />
          </Button>
          <Button
            type="button" size="sm" variant="ghost" className="rounded-none h-8 px-2"
            onClick={() => setShowSaveForm(false)}
          >
            <X size={14} />
          </Button>
        </div>
      ) : (
        <Button
          type="button" size="sm" variant="ghost" className="rounded-none text-xs h-7 px-2"
          onClick={() => setShowSaveForm(true)} data-testid={`${testIdPrefix}-save-btn`}
        >
          <Plus size={12} className="mr-1" /> Save current as preset
        </Button>
      )}
    </div>
  );
}

export default ImagePresetsRow;
